import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Briefcase, Key, Users, Map, ShieldCheck, Zap, AlertTriangle, BadgeCheck, TrendingDown, Lock, PhoneCall, IndianRupee, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const servicesList = [
  { icon: Key, title: 'Loan Origination', desc: 'End-to-end loan application processing and tracking system.' },
  { icon: ShieldCheck, title: 'Document Verification', desc: 'Secure AI-driven KYC and income documentation validation.' },
  { icon: Briefcase, title: 'B2B Partnerships', desc: 'Direct API integrations with 50+ leading banks and NBFCs.' },
  { icon: Users, title: 'Agent Network Management', desc: 'Empowering independent agents with digital tools and high commissions.' },
  { icon: Zap, title: 'Instant Approvals', desc: 'Our smart matching engine ensures fast in-principle loan sanction.' },
  { icon: Map, title: 'Pan-India Reach', desc: 'Coverage across Tier 1, 2, and 3 cities ensuring financial inclusion.' }
];

const costSavers = [
  {
    icon: AlertTriangle,
    color: 'rose',
    title: 'Extra Charges We Help You Avoid',
    items: [
      'Hidden processing fees not mentioned upfront',
      'Forced credit life or property insurance',
      'Prepayment penalties buried in fine print',
      'Excessive legal / stamp duty markup',
      'Unnecessary loan account opening charges',
    ]
  },
  {
    icon: BadgeCheck,
    color: 'emerald',
    title: 'What We Guarantee You Instead',
    items: [
      'Zero lender referral fee from you — ever',
      'Full sanction letter review before you sign',
      'Rate comparison across 50+ lenders',
      'EMI calculation with all real-cost inclusions',
      'Dedicated advisor until disbursement day',
    ]
  }
];

const safePaymentSteps = [
  { icon: Lock, title: 'Encrypted Document Vault', desc: 'All your KYC and income documents are stored with 256-bit AES encryption. Only RBI-licensed lenders can access them — with your permission.' },
  { icon: IndianRupee, title: 'Direct Bank-to-Customer Disbursement', desc: 'Loan amount is always transferred directly from the lender bank to your account. We never touch your money.' },
  { icon: PhoneCall, title: 'Dedicated Grievance Support', desc: 'If any lender charges you something unexpected, call our grievance desk. We\'ll resolve it within 48 hours — or escalate to RBI on your behalf.' },
];

const bankSelection = [
  { step: '1', label: 'Profile Analysis', desc: 'We analyse your income, CIBIL score, and loan requirement to profile your creditworthiness.' },
  { step: '2', label: 'Lender Shortlisting', desc: 'Our engine eliminates lenders with poor track records and shortlists the top 3 by rate + speed.' },
  { step: '3', label: 'Offer Negotiation', desc: 'Our team negotiates directly with the bank\'s relationship managers for the best possible terms.' },
  { step: '4', label: 'Final Recommendation', desc: 'You see a clear comparison with total cost of loan — not just EMI — before you decide.' },
];

const Services = () => {
  return (
    <div className="min-h-screen bg-background pb-24">

      {/* ── Hero ── */}
      <div className="bg-slate-900 text-white py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 opacity-80" />
        <div className="relative z-10 container mx-auto px-4">
          <span className="inline-block bg-emerald-500/15 text-emerald-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">What We Do</span>
          <h1 className="text-4xl md:text-5xl font-bold font-['Plus_Jakarta_Sans'] mb-4">Our Services</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto px-4">
            Comprehensive financial solutions for borrowers and agents alike — built on transparency, safety, and zero extra costs.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 mt-16 max-w-6xl">

        {/* ── Core Services Grid ── */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {servicesList.map((service, i) => (
            <Card key={i} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white dark:bg-slate-800 group">
              <CardContent className="p-8">
                <div className="h-14 w-14 rounded-2xl bg-emerald-100 dark:bg-emerald-500/10 flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors">
                  <service.icon className="h-7 w-7 text-emerald-600 dark:text-emerald-400 group-hover:text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 font-['Plus_Jakarta_Sans']">{service.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{service.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── NEW: Hidden Charges vs. Our Guarantee ── */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <span className="inline-block bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Cost Protection Service</span>
            <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-4">We guide you to prevent every extra rupee of cost</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Lenders use complex paperwork to hide fees. Our service cuts through that — so you never pay more than you should.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {costSavers.map((col, i) => (
              <div key={i} className={`rounded-2xl p-8 border ${i === 0 ? 'border-rose-200 dark:border-rose-900/50 bg-rose-50/50 dark:bg-rose-900/10' : 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/50 dark:bg-emerald-900/10'}`}>
                <div className={`flex items-center gap-3 mb-6`}>
                  <col.icon className={`h-6 w-6 ${i === 0 ? 'text-rose-500' : 'text-emerald-500'}`} />
                  <h3 className={`font-bold text-lg ${i === 0 ? 'text-rose-700 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400'}`}>{col.title}</h3>
                </div>
                <ul className="space-y-3">
                  {col.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${i === 0 ? 'text-rose-400' : 'text-emerald-500'}`} />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ── NEW: Safe Payment Infrastructure ── */}
        <div className="mb-24 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 p-10 md:p-16 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-sky-500 rounded-full blur-3xl opacity-10" />
          <div className="text-center mb-12 relative z-10">
            <span className="inline-block bg-sky-500/15 text-sky-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Payment Safety</span>
            <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-4">How we keep your payments 100% safe</h2>
            <p className="text-slate-400 max-w-xl mx-auto">Every rupee you pay goes exactly where it should — protected by bank-grade security and RBI compliance.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative z-10">
            {safePaymentSteps.map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:bg-white/10 transition-colors">
                <div className="h-12 w-12 rounded-xl bg-sky-500/20 flex items-center justify-center mb-5">
                  <s.icon className="h-6 w-6 text-sky-400" />
                </div>
                <h4 className="font-bold text-white mb-3">{s.title}</h4>
                <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── NEW: How We Choose the Best Bank For You ── */}
        <div className="mb-12">
          <div className="text-center mb-12">
            <span className="inline-block bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Smart Lender Matching</span>
            <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-4">We choose the best finance bank for you — not for us</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Our advisors earn the same fee regardless of which bank you choose. So our recommendation is always 100% in your interest.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {bankSelection.map((s, i) => (
              <div key={i} className="group bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl p-7 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="h-12 w-12 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mb-5 font-black text-indigo-600 dark:text-indigo-400 text-lg">{s.step}</div>
                <h4 className="font-bold text-slate-900 dark:text-white mb-2">{s.label}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold px-8" asChild>
              <Link to="/loan-products">Find My Best Loan <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Services;
